import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import CityModal from './CityModal';
import { showSuccess, showError } from '../../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface City {
  city_id: number;
  city_name: string;
  city_status: number;
}

const Cities: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<City | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Cancelled');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchCities = async () => {
    try {
      const data = await apiRequest<City[]>({
        method: 'get',
        url: 'city',
      });
      setCities(data);
    } catch (error) {
      showError('Failed to fetch cities');
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  const handleEditClick = (record: City) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchCities();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (city_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-city/${city_id}`,
      });
      showSuccess('City deleted');
      fetchCities();
    } catch {
      showError('Failed to delete city');
    }
  };

  const handleStatusToggle = async (city_id: number) => {
    setCities((prev) =>
      prev.map((item) =>
        item.city_id === city_id
          ? { ...item, city_status: item.city_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-city/${city_id}`,
      });
      showSuccess('City status updated');
    } catch {
      showError('Failed to update status');
      fetchCities();
    }
  };

  const handleTableChange: TableProps<City>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<City> = [
    {
      title: 'City Name',
      dataIndex: 'city_name',
      key: 'city_name',
      filters: Array.from(new Set(cities.map((s) => s.city_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filteredValue: filteredInfo.city_name || null,
      onFilter: (value, record) => record.city_name.includes(value as string),
      sorter: (a, b) => a.city_name.localeCompare(b.city_name),
      sortOrder: sortedInfo.columnKey === 'city_name' ? sortedInfo.order : null,
      ellipsis: true,
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_, record) => (
        <Space>
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checked={!!record.city_status}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                onChange={() => handleStatusToggle(record.city_id)}
              />
              <Button onClick={() => handleEditClick(record)}>
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this city?"
              onConfirm={() => handleDelete(record.city_id)}
              onCancel={cancel}
            >
              <Button danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Cities</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Button type="primary" onClick={showCreateModal}>
            Add City
          </Button>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<City>
          className={styles.customTable}
          columns={columns}
          rowKey="city_id"
          // Slice data based on current page and pageSize
          dataSource={cities.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          pagination={false} // Disable Ant Design built-in pagination
          bordered
          footer={() => (
            <Flex justify="space-between" align="center">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, cities.length)} of {cities.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={cities.length}
                showSizeChanger
                pageSizeOptions={['5', '10', '20', '50']}
                onChange={(page, pageSize) =>
                  setPagination({ current: page, pageSize: pageSize || pagination.pageSize })
                }
              />
            </Flex>
          )}
        />
      </div>

      <CityModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Cities;
